/**
 * Shared manuscript shape consumed by EPUB, DOCX, and the native PDF leaf
 * walk. Deliberately format-agnostic so none of them depend on another
 * format's builder, just on this common input type.
 */
import { assertNever } from "../lib/assertNever";
import type { ManuscriptItem, ZwChapter, ZwMapFigure } from "../lib/zwConfig";
import type { PreparedMapFigure } from "./mapFigureExport";

export type BookChapter = {
  name: string;
  /** Chapter body HTML: scenes via `zwdocToExportHtml`, joined by `assembleChapterHtml`. */
  html: string;
};

/** One top-level export unit in manuscript order (#177). */
export type BookBlock =
  | ({ readonly type: "chapter" } & BookChapter)
  | {
      readonly type: "map";
      readonly prepared: PreparedMapFigure;
    };

/** Pure walk order for export (acts flatten; maps stay in place). */
export type ManuscriptExportLeaf =
  | { readonly type: "chapter"; readonly chapter: ZwChapter }
  | { readonly type: "map"; readonly figure: ZwMapFigure };

export function manuscriptExportLeaves(
  manuscript: readonly ManuscriptItem[],
): ManuscriptExportLeaf[] {
  const out: ManuscriptExportLeaf[] = [];
  for (const item of manuscript) {
    switch (item.type) {
      case "chapter":
        out.push({ type: "chapter", chapter: item });
        break;
      case "act":
        for (const chapter of item.chapters) {
          out.push({ type: "chapter", chapter });
        }
        break;
      case "map":
        out.push({ type: "map", figure: item });
        break;
      default:
        assertNever(item);
    }
  }
  return out;
}

/** Chapters only (title fallback / Markdown). */
export function chaptersFromBlocks(
  blocks: readonly BookBlock[],
): BookChapter[] {
  const out: BookChapter[] = [];
  for (const block of blocks) {
    if (block.type === "chapter") {
      out.push({ name: block.name, html: block.html });
    }
  }
  return out;
}

export function blocksHaveExportableContent(
  blocks: readonly BookBlock[],
): boolean {
  return blocks.length > 0;
}
